import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAction, useQuery, useMutation } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { api } from '../convex';
import toast from 'react-hot-toast';

/**
 * Custom hook for GitHub integration using Convex backend
 * RULES OF HOOKS COMPLIANT: No conditional hooks, no hooks inside try-catch
 */
export const useConvexGitHub = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);

  // --- Actions: always called unconditionally ---
  const saveGithubTokenAction   = useAction(api.actions.github.saveGithubToken);
  const validateGithubTokenAction = useAction(api.actions.github.validateGithubToken);
  const makeCommitsAction       = useAction(api.actions.github.makeCommits);

  // --- Queries: always called unconditionally, skip via "skip" arg ---
  const githubStatus     = useQuery(api.github.getGithubStatus,    user?.id ? { userId: user.id } : "skip");
  const userRepos        = useQuery(api.github.getUserRepos,        user?.id ? { userId: user.id } : "skip");
  const currentStreakRepo = useQuery(api.github.getUserStreakRepo,  user?.id ? { userId: user.id, year: new Date().getFullYear() } : "skip");

  // ---- handlers ----

  const saveGithubToken = useCallback(async (token) => {
    if (!user?.id) { toast.error('❌ User not authenticated'); return null; }
    setLoading(true);
    try {
      const result = await saveGithubTokenAction({ userId: user.id, token });
      if (result.success) {
        toast.success('🎉 GitHub token saved successfully! Your GitHub integration is now active.');
        return result;
      } else {
        toast.error(`❌ ${result.error || 'Failed to save GitHub token. Please try again.'}`);
        return null;
      }
    } catch (error) {
      console.error('Error saving GitHub token:', error);
      toast.error('❌ Failed to save GitHub token. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [user?.id, saveGithubTokenAction]);

  const validateGithubToken = useCallback(async () => {
    if (!user?.id) return { valid: false, error: 'User not authenticated' };
    setLoading(true);
    try {
      return await validateGithubTokenAction({ userId: user.id });
    } catch (error) {
      console.error('Error validating GitHub token:', error);
      return { valid: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [user?.id, validateGithubTokenAction]);

  const makeCommits = useCallback(async (commitCount = 1, customMessage = null) => {
    if (!user?.id) { toast.error('❌ User not authenticated'); return null; }
    setLoading(true);
    try {
      const result = await makeCommitsAction({ userId: user.id, commitCount, customMessage });
      if (result.success) {
        toast.success(`🚀 Successfully made ${result.commitsCompleted} commits!`);
        return result;
      } else {
        toast.error(`❌ ${result.error || 'Failed to make commits. Please try again.'}`);
        return null;
      }
    } catch (error) {
      console.error('Error making commits:', error);
      toast.error('❌ Failed to make commits. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [user?.id, makeCommitsAction]);

  const getUserRepositories = useCallback(async () => {
    if (!user?.id) return { success: false, error: 'User not authenticated' };
    // userRepos is already fetched via useQuery above
    return { success: true, repos: userRepos || [] };
  }, [user?.id, userRepos]);

  const getGithubStatusInfo = useCallback(() => {
    if (!user?.id) return { connected: false, githubUsername: null, message: 'User not authenticated', loading: false };
    if (githubStatus === undefined) return { connected: false, githubUsername: null, message: 'Loading...', loading: true };
    return {
      connected: githubStatus?.connected || false,
      githubUsername: githubStatus?.githubUsername || null,
      avatarUrl: githubStatus?.avatarUrl || null,
      lastValidated: githubStatus?.lastValidated || null,
      message: githubStatus?.message || 'No GitHub token configured',
      loading: false,
    };
  }, [user?.id, githubStatus]);

  return {
    saveGithubToken,
    validateGithubToken,
    makeCommits,
    getUserRepositories,
    getGithubStatusInfo,
    githubStatus: getGithubStatusInfo(),
    userRepos,
    currentStreakRepo,
    loading,
  };
};

/**
 * Hook for GitHub commit settings using Convex
 * RULES OF HOOKS COMPLIANT
 */
export const useConvexCommitSettings = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);

  // Always called, skip via "skip"
  const streakData = useQuery(api.github.getStreakProgress, user?.id ? { userId: user.id } : "skip");
  const updateCommitSettingsMutation = useMutation(api.github.updateCommitSettings);

  const saveCommitSettings = useCallback(async (settings) => {
    if (!user?.id) { toast.error('❌ User not authenticated'); return null; }
    setLoading(true);
    try {
      await updateCommitSettingsMutation({
        userId: user.id,
        settings: {
          commitsPerDay: Math.min(settings.commits_per_day || 1, 5),
          randomCommits: settings.random_commits || false,
          commitTimeRange: { startHour: 9, endHour: 17 },
        }
      });
      toast.success('✅ Settings saved successfully!');
      return true;
    } catch (error) {
      console.error('Error saving commit settings:', error);
      toast.error('❌ Failed to save settings. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [user?.id, updateCommitSettingsMutation]);

  const getCommitSettings = useCallback(() => {
    if (!user?.id || !streakData?.settings) {
      return { commits_per_day: 1, random_commits: false, exclude_weekends: false, loading: streakData === undefined };
    }
    return {
      commits_per_day: streakData.settings.commitsPerDay || 1,
      random_commits: streakData.settings.randomCommits || false,
      exclude_weekends: false,
      loading: false,
    };
  }, [user?.id, streakData]);

  return {
    saveCommitSettings,
    getCommitSettings,
    commitSettings: getCommitSettings(),
    loading,
  };
};

/**
 * Hook for dashboard data using Convex
 * RULES OF HOOKS COMPLIANT — subscriptionPlanType from Redux used for correct goal
 */
export const useConvexDashboard = () => {
  const { user } = useUser();
  // Subscription plan type from Redux (always kept in sync)
  const subscriptionPlanType = useSelector((state) => state.subscription?.plan_type || 'monthly');

  // Always called, skip via "skip" arg
  const streakProgress = useQuery(api.github.getStreakProgress, user?.id ? { userId: user.id } : "skip");
  const commitHistory  = useQuery(api.github.getUserCommitHistory, user?.id ? { userId: user.id, limit: 365 } : "skip");

  const getStreakProgress = useCallback(() => {
    const defaultData = {
      currentStreak: 0, longestStreak: 0, totalCommits: 0,
      planType: 'monthly', streakGoal: 30, streakPercentage: 0,
      current_streak: 0, longest_streak: 0, commits_made: 0,
      streak_goal: 30, streak_percentage: 0, days_remaining: 30,
      loading: false,
    };

    if (!user?.id) return defaultData;
    if (streakProgress === undefined) return { ...defaultData, loading: true };

    // Priority: subscription plan type > streak data plan type
    const planType = subscriptionPlanType || streakProgress?.planType || 'monthly';
    const streakGoal = planType === 'yearly' ? 365 : 30;
    const currentStreak = streakProgress?.currentStreak || 0;
    const longestStreak = streakProgress?.longestStreak || 0;
    const totalCommits  = streakProgress?.totalCommits  ?? 0;
    const streakPercentage = Math.max(0, Math.min(100, Math.round((currentStreak / streakGoal) * 100)));
    const days_remaining  = Math.max(0, streakGoal - currentStreak);

    return {
      ...streakProgress,
      planType, streakGoal, streakPercentage,
      currentStreak, longestStreak, totalCommits,
      current_streak: currentStreak, longest_streak: longestStreak,
      commits_made: totalCommits,
      streak_goal: streakGoal, streak_percentage: streakPercentage,
      days_remaining,
      loading: false,
    };
  }, [user?.id, streakProgress, subscriptionPlanType]);

  const getCalendarData = useCallback(() => {
    if (!user?.id) return { calendar: [], summary: { total_days: 0, completed_days: 0, success_rate: 0 }, loading: false };
    if (commitHistory === undefined || streakProgress === undefined)
      return { calendar: [], summary: { total_days: 0, completed_days: 0, success_rate: 0 }, loading: true };

    const today = new Date();
    const calendar = [];
    const days = (subscriptionPlanType === 'yearly' || streakProgress?.planType === 'yearly') ? 365 : 30;

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayCommits = (commitHistory || []).filter(commit => {
        return new Date(commit.commitDate).toISOString().split('T')[0] === dateStr;
      });
      const successfulCommits = dayCommits.filter(c => c.success);
      let status = 'pending';
      if (date < today) status = successfulCommits.length > 0 ? 'completed' : 'missed';
      else if (date.toDateString() === today.toDateString()) status = successfulCommits.length > 0 ? 'completed' : 'pending';
      calendar.push({
        date: dateStr, status,
        commits_made: successfulCommits.length,
        day_of_week: date.toLocaleDateString('en-US', { weekday: 'long' }),
        is_weekend: date.getDay() === 0 || date.getDay() === 6,
      });
    }

    const completedDays = calendar.filter(d => d.status === 'completed').length;
    const missedDays    = calendar.filter(d => d.status === 'missed').length;
    const totalCommits  = calendar.reduce((sum, d) => sum + d.commits_made, 0);

    return {
      calendar: calendar.reverse(),
      summary: {
        total_days: calendar.length,
        completed_days: completedDays,
        missed_days: missedDays,
        success_rate: calendar.length > 0 ? Math.round((completedDays / calendar.length) * 100) : 0,
        total_commits: totalCommits,
        avg_commits_per_day: calendar.length > 0 ? Math.round((totalCommits / calendar.length) * 10) / 10 : 0,
      },
      loading: false,
    };
  }, [user?.id, commitHistory, streakProgress, subscriptionPlanType]);

  return {
    getStreakProgress,
    getCalendarData,
    streakProgress: getStreakProgress(),
    calendarData: getCalendarData(),
    commitHistory,
  };
};

export default useConvexGitHub;
